import React, { Component } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import { css } from 'react-emotion';
import isEqual from 'lodash/isEqual';
import { Accordion, Radio, RadioGroup, Select, Tooltip } from '@cimpress/react-components';

import { ALL, INDIVIDUAL, NONE } from '../../../constants/subscriptionConstants';
import { getCounterType } from '../../../reducers/identities/utils';
import Button from '../../common/Button';

const buttonStyle = css({
  marginLeft: '5px',
  textAlign: 'right'
});

const containerStyle = css({
  '.Select-menu-outer ': {
    position: 'relative'
  }
});

class SubscriptionAccordion extends Component {
  static propTypes = {
    counterParties: PropTypes.arrayOf(PropTypes.object).isRequired,
    identityType: PropTypes.string.isRequired,
    identityId: PropTypes.string.isRequired,
    identityName: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired,
    subscribedParties: PropTypes.arrayOf(PropTypes.object).isRequired,
    defaultSelectedNotification: PropTypes.string.isRequired,
    open: PropTypes.bool.isRequired,
    notificationType: PropTypes.string
  };

  constructor(props) {
    super(props);
    const { defaultSelectedNotification, subscribedParties, identityType } = this.props;

    this.state = {
      customOpen: this.props.open,
      selectedNotification: defaultSelectedNotification,
      selectedCounterParties: subscribedParties,
      counterPartyType: getCounterType(identityType)
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { defaultSelectedNotification, subscribedParties, identityType, identityId } = this.props;
    if (
      prevProps.identityId !== identityId ||
      prevProps.defaultSelectedNotification !== defaultSelectedNotification ||
      prevProps.subscribedParties !== subscribedParties
    ) {
      this.setState({
        selectedNotification: defaultSelectedNotification,
        selectedCounterParties: subscribedParties,
        counterPartyType: getCounterType(identityType),
        customOpen: false
      });
    }
  }

  onCancel = () => {
    const { defaultSelectedNotification, subscribedParties } = this.props;
    // return to default state
    this.setState({
      customOpen: false,
      selectedNotification: defaultSelectedNotification,
      selectedCounterParties: subscribedParties
    });
  };

  onChangeNotification = (e, value) => {
    this.setState({ selectedNotification: value });
  };

  onHeaderClick = () => {
    this.setState({ customOpen: !this.state.customOpen });
  };

  onSelect = selectedCounterParties => {
    this.setState({ selectedCounterParties });
  };

  getTitle = () => {
    const { t, defaultSelectedNotification, subscribedParties, identityName, identityType } = this.props;
    const counterPartyType = getCounterType(identityType);

    let text;

    if (defaultSelectedNotification === ALL) {
      text = (
        <span>
          <Trans i18nKey={`settings:subscriptions.subscriptionAccordion.notifiedOnAll_${counterPartyType}`}>
            text <b>bold text</b> more text
          </Trans>
        </span>
      );
    } else if (defaultSelectedNotification === INDIVIDUAL) {
      const numSubs = subscribedParties.length;
      text = (
        <span>
          <Trans
            i18nKey={`settings:subscriptions.subscriptionAccordion.notifiedOn_${counterPartyType}`}
            count={numSubs}>
            text <b>bold text</b> more text
          </Trans>
        </span>
      );
    } else {
      text = <span>{t('settings:subscriptions.subscriptionAccordion.noActiveNotifications')}</span>;
    }

    return (
      <span>
        <b>{identityName}</b> &nbsp; &nbsp;{text}
      </span>
    );
  };

  isSameSelection = () => {
    const { subscribedParties, defaultSelectedNotification } = this.props;
    const { selectedNotification, selectedCounterParties } = this.state;

    const isSameSelection = defaultSelectedNotification === selectedNotification;
    const hasSameSubscribedParties =
      !(isSameSelection && defaultSelectedNotification === INDIVIDUAL) ||
      isEqual(subscribedParties, selectedCounterParties);

    return isSameSelection && hasSameSubscribedParties;
  };

  onSave = () => {
    const { selectedNotification, selectedCounterParties } = this.state;
    const { identityId, identityType, onSave } = this.props;

    onSave(identityId, identityType, selectedNotification, selectedCounterParties.map(({ value }) => value));
  };

  render() {
    const { t, counterParties, notificationType } = this.props;
    const { selectedNotification, customOpen, selectedCounterParties, counterPartyType } = this.state;

    const title = this.getTitle();
    const disableSelect = selectedNotification !== INDIVIDUAL;
    const shownCounterParties = disableSelect ? [] : selectedCounterParties;

    const nothingSelected = selectedNotification === INDIVIDUAL && selectedCounterParties.length === 0;
    const sameAsBefore = this.isSameSelection();

    const disableSave = sameAsBefore || nothingSelected;
    const disableSaveTooltipMsg = sameAsBefore
      ? t('settings:subscriptions.subscriptionAccordion.noChangesWarning')
      : t('settings:subscriptions.subscriptionAccordion.noSelectionWarning', { context: counterPartyType });

    const saveButton = (
      <Button className={`btn btn-primary ${buttonStyle}`} disabled={disableSave} onClick={this.onSave}>
        {t('common:commonWords.save')}
      </Button>
    );

    const cancelButton = (
      <Button className={`btn btn-default ${buttonStyle}`} onClick={this.onCancel}>
        {t('common:commonWords.cancel')}
      </Button>
    );

    return (
      <div className={containerStyle}>
        <Accordion title={title} customOpen={customOpen} onHeaderClick={this.onHeaderClick}>
          <RadioGroup name="counterParties" onChange={this.onChangeNotification} valueSelected={selectedNotification}>
            <Radio
              label={t('settings:subscriptions.actions.subscribeToAll', {
                context: `${counterPartyType}_${notificationType}`
              })}
              value={ALL}
            />
            <Radio
              label={t('settings:subscriptions.actions.unsubscribeToAll', {
                context: `${counterPartyType}_${notificationType}`
              })}
              value={NONE}
            />
            <Radio
              label={t('settings:subscriptions.actions.subscribeToIndividual', {
                context: `${counterPartyType}_${notificationType}`
              })}
              value={INDIVIDUAL}
            />
          </RadioGroup>
          <Select
            label={t('settings:subscriptions.selectLabel', { context: counterPartyType })}
            value={shownCounterParties}
            options={counterParties}
            disabled={disableSelect}
            onChange={this.onSelect}
            multi
          />
          <div className={buttonStyle}>
            {disableSave ? <Tooltip contents={disableSaveTooltipMsg}>{saveButton}</Tooltip> : saveButton}
            {cancelButton}
          </div>
        </Accordion>
      </div>
    );
  }
}

export default withTranslation()(SubscriptionAccordion);
